import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { client } from '../lib/sanity'
import { motion } from 'framer-motion'
import FilterSelect from '../components/FilterSelect'
import Input from '../components/ui/Input'
import Button from '../components/ui/Button'
import Badge from '../components/ui/Badge'

// --- Helpers ---
const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.06 } } }
const item = { hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } }

function useDebouncedValue(value, delay = 250) {
    const [v, setV] = useState(value)
    useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t) }, [value, delay])
    return v
}

function plainTextFrom(post) {
    const md = post?.markdownContent || ''
    if (md) return md.replace(/[#>*_`\-[\]()!]|\n/g, ' ').replace(/\s+/g, ' ').trim()
    const pt = post?.content?.[0]?.children?.map(c => c.text).join(' ') || ''
    return pt
}

function excerpt(text, len = 160) {
    if (!text) return ''
    return text.length > len ? text.slice(0, len).trim() + '…' : text
}

function highlight(text, q) {
    if (!q) return text
    const parts = text.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig'))
    return parts.map((p, i) => p.toLowerCase() === q.toLowerCase() ? <mark key={i} className="bg-accent px-0.5">{p}</mark> : <span key={i}>{p}</span>)
}

export default function Blog() {
    const navigate = useNavigate()
    const [blogs, setBlogs] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedCategory, setSelectedCategory] = useState('')
    const [selectedSubcategory, setSelectedSubcategory] = useState('')
    const [selectedTag, setSelectedTag] = useState('')
    const [sort, setSort] = useState('newest') // newest | oldest | title-asc

    // infinite scroll
    const [visibleCount, setVisibleCount] = useState(9)
    const sentinelRef = useRef(null)

    useEffect(() => {
        setLoading(true)
        client.fetch(`*[_type == "blog"]|order(_createdAt desc){
      _id,
      _createdAt,
      title,
      slug,
      author,
      "image": mainImage.asset->url,
      category->{title},
      subcategory->{title},
      tags,
      content,
      markdownContent,
      isPresentation
    }`).then((data) => {
            setBlogs(data || [])
        }).finally(() => setLoading(false))
    }, [])

    // sticky controls shadow
    const controlsRef = useRef(null)
    const [elevated, setElevated] = useState(false)
    useEffect(() => {
        const onScroll = () => {
            if (!controlsRef.current) return
            setElevated(window.scrollY > 10)
        }
        window.addEventListener('scroll', onScroll, { passive: true })
        onScroll()
        return () => window.removeEventListener('scroll', onScroll)
    }, [])


    // build filters
    const categories = useMemo(() => {
        const s = new Set(blogs.map(b => b.category?.title).filter(Boolean))
        return Array.from(s).sort((a, b) => a.localeCompare(b))
    }, [blogs])
    const subcategories = useMemo(() => {
        const s = new Set(blogs.map(b => b.subcategory?.title).filter(Boolean))
        return Array.from(s).sort((a, b) => a.localeCompare(b))
    }, [blogs])
    const tags = useMemo(() => {
        const s = new Set()
        blogs.forEach(b => (b.tags || []).forEach(t => t && s.add(t)))
        return Array.from(s).sort((a, b) => a.localeCompare(b))
    }, [blogs])

    // search & sort
    const debounced = useDebouncedValue(searchTerm, 250)
    const filtered = useMemo(() => {
        const q = debounced.trim().toLowerCase()
        let list = blogs.filter(b => {
            const text = plainTextFrom(b).toLowerCase()
            const matchText = !q || b.title.toLowerCase().includes(q) || b.author?.toLowerCase().includes(q) || text.includes(q)
            const matchCat = !selectedCategory || b.category?.title === selectedCategory
            const matchSub = !selectedSubcategory || b.subcategory?.title === selectedSubcategory
            const matchTag = !selectedTag || (b.tags || []).includes(selectedTag)
            return matchText && matchCat && matchSub && matchTag
        })
        if (sort === 'oldest') list.sort((a, b) => new Date(a._createdAt) - new Date(b._createdAt))
        else if (sort === 'title-asc') list.sort((a, b) => a.title.localeCompare(b.title))
        else list.sort((a, b) => new Date(b._createdAt) - new Date(a._createdAt))
        return list
    }, [blogs, debounced, selectedCategory, selectedSubcategory, selectedTag, sort])

    // infinite scroll observer
    useEffect(() => {
        const el = sentinelRef.current
        if (!el) return
        const io = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) setVisibleCount(v => Math.min(v + 9, filtered.length))
        }, { rootMargin: '200px' })
        io.observe(el)
        return () => io.disconnect()
    }, [filtered.length])

    // slice for view
    const toShow = filtered.slice(0, visibleCount)

    return (
        <div className="min-h-screen bg-white dark:bg-zinc-950">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                    <h1 className="text-5xl md:text-6xl font-serif font-bold text-black dark:text-white mb-10 max-w-2xl">Blog</h1>

                    {/* Sticky Controls */}
                    <div ref={controlsRef} className={`sticky top-0 z-30 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-white/95 dark:bg-zinc-950/95 backdrop-blur border-b-2 ${elevated ? 'border-black dark:border-white shadow-brutal-sm' : 'border-transparent'}`}>
                        <div className="flex flex-wrap items-center gap-3 justify-between">
                            <Input
                                type="text"
                                placeholder="Search blog posts"
                                value={searchTerm}
                                onChange={e => { setSearchTerm(e.target.value); setVisibleCount(9) }}
                                className="flex-1 min-w-[200px] max-w-xl"
                            />
                            <div className="flex flex-wrap gap-2 items-center">
                                <FilterSelect value={selectedCategory} onChange={e => { setSelectedCategory(e.target.value); setVisibleCount(9) }} className="w-auto">
                                    <option value="">All Categories</option>
                                    {categories.map((c, i) => (<option key={i} value={c}>{c}</option>))}
                                </FilterSelect>
                                <FilterSelect value={selectedSubcategory} onChange={e => { setSelectedSubcategory(e.target.value); setVisibleCount(9) }} className="w-auto">
                                    <option value="">All Subcategories</option>
                                    {subcategories.map((s, i) => (<option key={i} value={s}>{s}</option>))}
                                </FilterSelect>
                                <FilterSelect value={selectedTag} onChange={e => { setSelectedTag(e.target.value); setVisibleCount(9) }} className="w-auto">
                                    <option value="">All Tags</option>
                                    {tags.map((t, i) => (<option key={i} value={t}>{t}</option>))}
                                </FilterSelect>
                                <FilterSelect value={sort} onChange={e => { setSort(e.target.value); setVisibleCount(9) }} className="w-auto">
                                    <option value="newest">Newest</option>
                                    <option value="oldest">Oldest</option>
                                    <option value="title-asc">Title A–Z</option>
                                </FilterSelect>
                                {(selectedCategory || selectedSubcategory || selectedTag || searchTerm) && (
                                    <Button variant="secondary" onClick={() => { setSelectedCategory(''); setSelectedSubcategory(''); setSelectedTag(''); setSearchTerm(''); setSort('newest'); setVisibleCount(9) }}>Reset</Button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Loading skeleton */}
                    {loading && (
                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {Array.from({ length: 9 }).map((_, i) => (
                                <div key={i} className="border-2 border-black dark:border-white bg-white dark:bg-zinc-950 h-[340px] animate-pulse" />
                            ))}
                        </div>
                    )}

                    {/* Empty State */}
                    {!loading && filtered.length === 0 && (
                        <div className="text-center text-zinc-700 dark:text-zinc-300 py-16">
                            <p className="text-lg font-medium">No posts match your filters.</p>
                            <div className="mt-4 flex justify-center">
                                <Button variant="secondary" onClick={() => { setSelectedCategory(''); setSelectedSubcategory(''); setSelectedTag(''); setSearchTerm(''); setSort('newest'); setVisibleCount(9) }}>Clear filters</Button>
                            </div>
                        </div>
                    )}

                    {/* Grid */}
                    {!loading && filtered.length > 0 && (
                        <motion.div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" variants={container} initial="hidden" animate="show">
                            {toShow.map((post, i) => {
                                const text = plainTextFrom(post)
                                const ex = excerpt(text)
                                const q = searchTerm.trim()
                                return (
                                    <motion.article
                                        key={post._id || i}
                                        variants={item}
                                        whileHover={{ y: -2 }}
                                        className={`group border-2 border-black dark:border-white bg-white dark:bg-zinc-950 shadow-brutal-sm overflow-hidden hover:shadow-brutal transition-shadow cursor-pointer ${i === 0 ? 'sm:col-span-2 lg:col-span-2 sm:flex' : ''}`}
                                        role="link"
                                        tabIndex={0}
                                        aria-label={`Open post: ${post.title}`}
                                        onClick={() => navigate(`/blog/${post.slug.current}`)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/blog/${post.slug.current}`) }}
                                    >
                                        {post.image && (
                                            <div className={`relative overflow-hidden border-b-2 sm:border-b-0 border-black dark:border-white ${i === 0 ? 'h-56 sm:h-auto sm:w-1/2 sm:border-r-2' : 'h-44 w-full'}`}>
                                                <img src={post.image} alt={post.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                                                <div className="absolute top-2 left-2 flex items-center gap-2">
                                                    {post.isPresentation && (
                                                        <Badge tone="accent">Presentation</Badge>
                                                    )}
                                                    {post.category?.title && (
                                                        <Badge>{post.category.title}</Badge>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        <div className={`p-4 ${i === 0 ? 'sm:w-1/2 sm:flex sm:flex-col sm:justify-center' : ''}`}>
                                            <h3 className={`font-serif font-bold text-black dark:text-white group-hover:text-accent line-clamp-2 ${i === 0 ? 'text-2xl' : 'text-lg'}`}>{q ? highlight(post.title, q) : post.title}</h3>
                                            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
                                                {post.subcategory?.title && <span>{post.subcategory.title} · </span>}
                                                {new Date(post._createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </p>
                                            <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-300 line-clamp-3">{q ? highlight(ex, q) : ex}</p>
                                            {/* Tags */}
                                            {!!(post.tags && post.tags.length) && (
                                                <div className="mt-3 flex flex-wrap gap-2">
                                                    {post.tags.slice(0, 4).map((t, idx) => (
                                                        <span key={idx} className="text-xs font-mono px-2 py-0.5 border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300">#{t}</span>
                                                    ))}
                                                </div>
                                            )}
                                            <Link
                                                to={`/blog/${post.slug.current}`}
                                                onClick={(e) => e.stopPropagation()}
                                                className="mt-4 inline-flex items-center text-accent hover:underline decoration-2"
                                            >
                                                Read more ↗
                                            </Link>
                                        </div>
                                    </motion.article>
                                )
                            })}
                        </motion.div>
                    )}

                    {/* Infinite scroll sentinel */}
                    {!loading && visibleCount < filtered.length && (
                        <div ref={sentinelRef} className="h-10" />
                    )}
                </div>
            </div>
    )
}